import React, { useState } from 'react';
import { Key, Bell, Lock, Smartphone, CheckCircle2, Mail, Phone, Volume2, VolumeX, LogOut, Monitor, Clock, AlertCircle, Save, X, Shield } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface SettingsSecurityTabProps {
  showToast: (msg: string) => void;
  initialTab?: 'general' | 'security';
}
export function SettingsSecurityTab({
  showToast,
  initialTab = 'general'
}: SettingsSecurityTabProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'security'>(initialTab);
  // General
  const [firstName, setFirstName] = useState('Елена');
  const [lastName, setLastName] = useState('Смирнова');
  const [email, setEmail] = useState('elena@example.com');
  const [emailVerified, setEmailVerified] = useState(true);
  const [emailVerifySent, setEmailVerifySent] = useState(false);
  const [phone, setPhone] = useState('+7 (999) 123-45-67');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showPhoneCode, setShowPhoneCode] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  // Notifications
  const [notifications, setNotifications] = useState([{
    id: 'orders',
    label: 'Новые заказы',
    email: true,
    push: true
  }, {
    id: 'messages',
    label: 'Сообщения в чате',
    email: false,
    push: true
  }, {
    id: 'reviews',
    label: 'Отзывы клиентов',
    email: true,
    push: false
  }, {
    id: 'system',
    label: 'Системные',
    email: true,
    push: true
  }]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notifFrequency, setNotifFrequency] = useState<'instant' | 'daily'>('instant');
  // Security
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFaEnabled, setTwoFaEnabled] = useState(true);
  const [sessions] = useState([{
    id: 1,
    device: 'Chrome · macOS',
    location: 'Москва, Россия',
    time: 'Сейчас',
    current: true
  }, {
    id: 2,
    device: 'Safari · iPhone',
    location: 'Москва, Россия',
    time: '2 часа назад',
    current: false
  }, {
    id: 3,
    device: 'Firefox · Windows',
    location: 'Санкт-Петербург, Россия',
    time: '3 дня назад',
    current: false
  }]);
  const toggleNotif = (id: string, field: 'email' | 'push') => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      [field]: !n[field]
    } : n));
    showToast('Настройка сохранена');
  };
  const handleSaveGeneral = () => {
    if (!firstName.trim() || !lastName.trim()) {
      showToast('⚠️ Заполните имя и фамилию');
      return;
    }
    if (!email.trim()) {
      showToast('⚠️ Укажите email');
      return;
    }
    showToast('Данные сохранены ✓');
  };
  const handleChangePassword = () => {
    if (!currentPassword) {
      showToast('⚠️ Введите текущий пароль');
      return;
    }
    if (newPassword.length < 8) {
      showToast('⚠️ Пароль минимум 8 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('⚠️ Пароли не совпадают');
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    showToast('Пароль изменён ✓');
  };
  const handleVerifyPhone = () => {
    if (phoneCode === '1234') {
      setPhoneVerified(true);
      setShowPhoneCode(false);
      setPhoneCode('');
      showToast('Телефон подтверждён ✓');
    } else {
      showToast('⚠️ Неверный код. Попробуйте 1234');
    }
  };
  return <div className="space-y-5 pb-20 sm:pb-6" data-id="element-14805">
      <div data-id="element-14806">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-14807">Настройки</h2>
        <p className="text-slate-500 text-sm mt-0.5" data-id="element-14808">
          Аккаунт, уведомления, безопасность
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200" data-id="element-14809">
        {[{
        key: 'general' as const,
        label: 'Общие'
      }, {
        key: 'security' as const,
        label: 'Безопасность'
      }].map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-14810">
            {tab.label}
          </button>)}
      </div>

      {/* GENERAL TAB */}
      {activeTab === 'general' && <div className="max-w-2xl space-y-5" data-id="element-14811">
          <Card padding="md" data-id="element-14812">
            <h3 className="font-bold text-slate-900 mb-4" data-id="element-14813">Личные данные</h3>
            <div className="space-y-3" data-id="element-14814">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14815">
                <div data-id="element-14816">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14817">
                    Имя
                  </label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14818" />
                </div>
                <div data-id="element-14819">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14820">
                    Фамилия
                  </label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14821" />
                </div>
              </div>

              {/* Email */}
              <div data-id="element-14822">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14823">
                  Email
                </label>
                <div className="flex gap-2" data-id="element-14824">
                  <input type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                setEmailVerified(false);
                setEmailVerifySent(false);
              }} className="flex-1 rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14825" />
                  {emailVerified ? <Badge variant="success" className="shrink-0 self-center" data-id="element-14826">
                      <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-14827" /> Подтверждён
                    </Badge> : emailVerifySent ? <Badge variant="warning" className="shrink-0 self-center" data-id="element-14828">
                      <Clock className="w-3 h-3 mr-1" data-id="element-14829" /> Отправлено
                    </Badge> : <Button variant="outline" size="sm" className="shrink-0" onClick={() => {
                setEmailVerifySent(true);
                showToast('Письмо отправлено на ' + email);
              }} data-id="element-14830">
                      Подтвердить
                    </Button>}
                </div>
              </div>

              {/* Phone */}
              <div data-id="element-14831">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14832">
                  Телефон
                </label>
                <div className="flex gap-2" data-id="element-14833">
                  <input type="tel" value={phone} onChange={e => {
                setPhone(e.target.value);
                setPhoneVerified(false);
              }} className="flex-1 rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14834" />
                  {phoneVerified ? <Badge variant="success" className="shrink-0 self-center" data-id="element-14835">
                      <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-14836" /> Подтверждён
                    </Badge> : <Button variant="outline" size="sm" className="shrink-0" onClick={() => {
                setShowPhoneCode(true);
                showToast('Код отправлен (тест: 1234)');
              }} data-id="element-14837">
                      Подтвердить
                    </Button>}
                </div>
                {showPhoneCode && !phoneVerified && <div className="flex gap-2 mt-2" data-id="element-14838">
                    <input type="text" value={phoneCode} onChange={e => setPhoneCode(e.target.value)} placeholder="Код" maxLength={4} className="w-28 rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-center tracking-widest" onKeyDown={e => {
                if (e.key === 'Enter') handleVerifyPhone();
              }} data-id="element-14839" />
                    <Button variant="primary" size="sm" onClick={handleVerifyPhone} data-id="element-14840">
                      Проверить
                    </Button>
                    <button onClick={() => {
                setShowPhoneCode(false);
                setPhoneCode('');
              }} className="p-2 text-slate-400 hover:text-slate-600" data-id="element-14841">
                      <X className="w-4 h-4" data-id="element-14842" />
                    </button>
                  </div>}
              </div>

              <div className="pt-2" data-id="element-14843">
                <Button variant="primary" icon={<Save className="w-4 h-4" data-id="element-14845" />} onClick={handleSaveGeneral} data-id="element-14844">
                  Сохранить изменения
                </Button>
              </div>
            </div>
          </Card>

          {/* Notifications */}
          <Card padding="md" data-id="element-14846">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14847">
              <Bell className="w-5 h-5 text-slate-400" data-id="element-14848" /> Уведомления
            </h3>
            <div className="space-y-2 mb-4" data-id="element-14849">
              {notifications.map(n => <div key={n.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0" data-id="element-14850">
                  <span className="text-sm font-medium text-slate-700" data-id="element-14851">
                    {n.label}
                  </span>
                  <div className="flex gap-3" data-id="element-14852">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" data-id="element-14853">
                      <input type="checkbox" checked={n.email} onChange={() => toggleNotif(n.id, 'email')} className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-14854" />{' '}
                      Email
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" data-id="element-14855">
                      <input type="checkbox" checked={n.push} onChange={() => toggleNotif(n.id, 'push')} className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4" data-id="element-14856" />{' '}
                      Push
                    </label>
                  </div>
                </div>)}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 pt-3 border-t border-slate-100" data-id="element-14857">
              <div className="flex items-center gap-3" data-id="element-14858">
                <button onClick={() => {
              setSoundEnabled(!soundEnabled);
              showToast(soundEnabled ? 'Звук выключен' : 'Звук включён');
            }} className={`p-2 rounded-lg border transition-colors ${soundEnabled ? 'border-indigo-200 bg-indigo-50 text-indigo-600' : 'border-slate-200 text-slate-400'}`} data-id="element-14859">
                  {soundEnabled ? <Volume2 className="w-4 h-4" data-id="element-14860" /> : <VolumeX className="w-4 h-4" data-id="element-14861" />}
                </button>
                <span className="text-sm text-slate-700" data-id="element-14862">
                  {soundEnabled ? 'Звук вкл' : 'Звук выкл'}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-14863">
                <span className="text-xs text-slate-500" data-id="element-14864">Частота:</span>
                <div className="flex bg-slate-100 p-0.5 rounded-lg" data-id="element-14865">
                  {(['instant', 'daily'] as const).map(f => <button key={f} onClick={() => {
                setNotifFrequency(f);
                showToast(`Частота: ${f === 'instant' ? 'сразу' : 'раз в день'}`);
              }} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${notifFrequency === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-id="element-14866">
                      {f === 'instant' ? 'Сразу' : 'Раз в день'}
                    </button>)}
                </div>
              </div>
            </div>
          </Card>
        </div>}

      {/* SECURITY TAB */}
      {activeTab === 'security' && <div className="max-w-2xl space-y-5" data-id="element-14867">
          {/* Password */}
          <Card padding="md" data-id="element-14868">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14869">
              <Key className="w-5 h-5 text-slate-400" data-id="element-14870" /> Смена пароля
            </h3>
            <div className="space-y-3" data-id="element-14871">
              <div data-id="element-14872">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14873">
                  Текущий пароль
                </label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" placeholder="••••••••" data-id="element-14874" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14875">
                <div data-id="element-14876">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14877">
                    Новый пароль
                  </label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" placeholder="Минимум 8 символов" data-id="element-14878" />
                </div>
                <div data-id="element-14879">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14880">
                    Повторите
                  </label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full rounded-lg px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm ${confirmPassword && confirmPassword !== newPassword ? 'border-rose-300 bg-rose-50' : 'border-slate-200'}`} placeholder="••••••••" data-id="element-14881" />
                </div>
              </div>
              {newPassword && newPassword.length < 8 && <p className="text-xs text-rose-500 flex items-center gap-1" data-id="element-14882">
                  <AlertCircle className="w-3 h-3" data-id="element-14883" /> Минимум 8 символов
                </p>}
              {confirmPassword && confirmPassword !== newPassword && <p className="text-xs text-rose-500 flex items-center gap-1" data-id="element-14884">
                  <AlertCircle className="w-3 h-3" data-id="element-14885" /> Пароли не совпадают
                </p>}
              <Button variant="primary" onClick={handleChangePassword} data-id="element-14886">
                Обновить пароль
              </Button>
            </div>
          </Card>

          {/* 2FA */}
          <Card padding="md" data-id="element-14887">
            <div className="flex justify-between items-start mb-3" data-id="element-14888">
              <div data-id="element-14889">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14890">
                  <Smartphone className="w-5 h-5 text-slate-400" data-id="element-14891" />{' '}
                  Двухфакторная аутентификация
                </h3>
                <p className="text-xs text-slate-500 mt-1" data-id="element-14892">
                  Дополнительная защита при входе
                </p>
              </div>
              <Badge variant={twoFaEnabled ? 'success' : 'warning'} size="sm" data-id="element-14893">
                {twoFaEnabled ? 'Включена' : 'Выключена'}
              </Badge>
            </div>
            <div className="flex gap-2" data-id="element-14894">
              <Button variant={twoFaEnabled ? 'outline' : 'primary'} size="sm" onClick={() => {
            setTwoFaEnabled(!twoFaEnabled);
            showToast(twoFaEnabled ? '2FA выключена' : '2FA включена ✓');
          }} data-id="element-14895">
                {twoFaEnabled ? 'Выключить' : 'Включить'}
              </Button>
              {twoFaEnabled && <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => showToast('Откройте приложение-аутентификатор и отсканируйте QR-код')} data-id="element-14896">
                  Настроить
                </Button>}
            </div>
          </Card>

          {/* Active Sessions */}
          <Card padding="md" data-id="element-14897">
            <div className="flex justify-between items-center mb-4" data-id="element-14898">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14899">
                <Monitor className="w-5 h-5 text-slate-400" data-id="element-14900" /> Активные сессии
              </h3>
              <Button variant="ghost" size="sm" className="text-rose-600" icon={<LogOut className="w-3.5 h-3.5" data-id="element-14902" />} onClick={() => showToast('Выход со всех устройств')} data-id="element-14901">
                Выйти со всех
              </Button>
            </div>
            <div className="space-y-2" data-id="element-14903">
              {sessions.map(s => <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border ${s.current ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'}`} data-id="element-14904">
                  <div className="flex items-center gap-3" data-id="element-14905">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.current ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-14906">
                      {s.device.includes('iPhone') ? <Smartphone className="w-4 h-4" data-id="element-14907" /> : <Monitor className="w-4 h-4" data-id="element-14908" />}
                    </div>
                    <div data-id="element-14909">
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-14910">
                        {s.device}
                        {s.current && <Badge variant="success" size="sm" data-id="element-14911">
                            Текущая
                          </Badge>}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-14912">
                        {s.location} · {s.time}
                      </div>
                    </div>
                  </div>
                  {!s.current && <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => showToast('Сессия завершена')} data-id="element-14913">
                      Завершить
                    </Button>}
                </div>)}
            </div>
          </Card>
        </div>}

      {/* Mobile Sticky Save (General tab) */}
      {activeTab === 'general' && <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-slate-200 sm:hidden z-40 shadow-lg" data-id="element-14914">
          <Button variant="primary" fullWidth icon={<Save className="w-4 h-4" data-id="element-14916" />} onClick={handleSaveGeneral} data-id="element-14915">
            Сохранить
          </Button>
        </div>}
    </div>;
}